/**
 * Migas de pan a partir de la entrada activa del registro: el espacio (texto,
 * sin enlace), la entrada (enlace a su pantalla) y lo que cuelga debajo (un
 * detalle, un formulario). La sección no se muestra: el espacio ya orienta.
 *
 * Si una dirección no tiene entrada activa se arma desde sus segmentos, como
 * antes; la prueba de cobertura de rutas hace que eso no pase con rutas reales.
 * Función pura, sin React.
 */
import { NAV_META } from '@/copy/nav';

import { resolverEntradaActiva, type Ubicacion } from './entrada-activa';
import type { Seccion } from './registro';

export interface Miga {
  label: string;
  /** Sin `to`, la miga es solo texto (el espacio no tiene pantalla propia). */
  to?: string;
}

/** Segmentos que quedan por debajo de una entrada. Los demás caen a un respaldo. */
const SEGMENTOS: Record<string, string> = {
  new: 'Nuevo',
  nuevo: 'Nuevo',
  edit: 'Editar',
  ai: 'Con IA',
  campaign: 'Campaña',
  'from-job': 'Desde una búsqueda',
  contactos: 'Contactos',
  directorio: 'Directorio',
  profile: 'Perfil',
  security: 'Seguridad',
  notifications: 'Notificaciones',
  signatures: 'Firmas',
  telegram: 'Telegram',
  launcher: 'Inicio rápido',
  calendario: 'Calendario',
  'compartir-prospectos': 'Compartir prospectos',
  'mi-ia': 'Tu contexto de IA',
  vocabulary: 'Vocabulario',
  overview: 'Resumen',
  etapas: 'Etapas',
  bitacora: 'Bitácora',
  reuniones: 'Reuniones',
  decisiones: 'Decisiones',
  stack: 'Tecnología',
  docs: 'Documentos',
  backlog: 'Backlog',
  soporte: 'Soporte',
  workflows: 'Automatizaciones',
  workers: 'Workers',
  edge: 'Funciones edge',
  integrations: 'Integraciones',
  chat: 'Chat',
  pdfs: 'PDFs',
  // Conexiones vive dentro de Ajustes (la entrada lo cubre con `cubre`).
  connections: 'Tu correo y calendario',
};

/**
 * Debajo de una entrada, todo segmento que no sea uno de los conocidos es un
 * identificador (un número, un UUID, un slug como `demo` o `SOL-12`): se
 * muestra como «Detalle» y nunca la parte técnica de la dirección.
 */
function etiquetaDeSegmento(segmento: string): string {
  return SEGMENTOS[segmento] ?? 'Detalle';
}

/** Respaldo sin entrada activa: arma el texto desde el segmento, como antes. */
function etiquetaDeRespaldo(segmento: string): string {
  if (SEGMENTOS[segmento]) return SEGMENTOS[segmento];
  if (/^\d+$/.test(segmento) || /^[0-9a-f-]{16,}$/i.test(segmento)) return 'Detalle';
  return segmento.charAt(0).toUpperCase() + segmento.slice(1).replace(/-/g, ' ');
}

const segmentosDe = (path: string) => path.split('/').filter(Boolean);

export function construirMigas(registro: Seccion[], ubicacion: Ubicacion): Miga[] {
  const segmentos = segmentosDe(ubicacion.pathname);
  const activa = resolverEntradaActiva(registro, ubicacion);

  if (!activa) {
    return segmentos.map((s, i) => ({
      label: etiquetaDeRespaldo(s),
      to: `/${segmentos.slice(0, i + 1).join('/')}`,
    }));
  }

  const migas: Miga[] = [];
  if (activa.espacio) migas.push({ label: NAV_META[activa.espacio.id]?.label ?? activa.espacio.id });
  migas.push({
    label: NAV_META[activa.entrada.id]?.label ?? activa.entrada.id,
    to: activa.entrada.to,
  });
  // Una dirección que la entrada cubre sin colgar de la suya (`/connections`
  // bajo Ajustes) cuelga entera: todos sus segmentos van después de la entrada.
  const propios = segmentosDe(activa.entrada.to.split('?')[0]);
  const cuelga = propios.every((p, i) => segmentos[i] === p);
  const base = cuelga ? activa.segmentosBase : 0;
  const finDeParametros = cuelga ? base + activa.segmentosDeParametro : 0;
  for (let i = base; i < segmentos.length; i += 1) {
    migas.push({
      // El id que declara el patrón (`:agentKey`) siempre es un detalle.
      label: i < finDeParametros ? 'Detalle' : etiquetaDeSegmento(segmentos[i]),
      to: `/${segmentos.slice(0, i + 1).join('/')}`,
    });
  }
  return migas;
}
